import { useEffect, useState } from 'react';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

const countryCodes = [
  { code: '+263', country: 'ZW', flag: '🇿🇼', name: 'Zimbabwe' },
  { code: '+27', country: 'ZA', flag: '🇿🇦', name: 'South Africa' },
  { code: '+258', country: 'MZ', flag: '🇲🇿', name: 'Mozambique' },
  { code: '+260', country: 'ZM', flag: '🇿🇲', name: 'Zambia' },
  { code: '+267', country: 'BW', flag: '🇧🇼', name: 'Botswana' },
  { code: '+265', country: 'MW', flag: '🇲🇼', name: 'Malawi' },
  { code: '+264', country: 'NA', flag: '🇳🇦', name: 'Namibia' },
  { code: '+244', country: 'AO', flag: '🇦🇴', name: 'Angola' },
  { code: '+243', country: 'CD', flag: '🇨🇩', name: 'DR Congo' },
  { code: '+255', country: 'TZ', flag: '🇹🇿', name: 'Tanzania' },
  { code: '+254', country: 'KE', flag: '🇰🇪', name: 'Kenya' },
  { code: '+256', country: 'UG', flag: '🇺🇬', name: 'Uganda' },
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'USA' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'UK' },
];

export const PhoneInput = ({ 
  value = '', 
  onChange, 
  placeholder = 'Phone number',
  required = false,
  disabled = false,
  className = '',
  'data-testid': dataTestId
}) => {
  // Parse initial value to extract country code
  const parsePhone = (phone) => {
    if (!phone) return { countryCode: '+263', number: '' };
    
    // Find matching country code
    for (const cc of countryCodes) {
      if (phone.startsWith(cc.code)) {
        return { 
          countryCode: cc.code, 
          number: phone.slice(cc.code.length).replace(/\s/g, '') 
        };
      }
    }
    
    // Default to Zimbabwe if no match
    return { countryCode: '+263', number: phone.replace(/^\+/, '').replace(/\s/g, '') };
  };

  const { countryCode: initialCode, number: initialNumber } = parsePhone(value);
  const [countryCode, setCountryCode] = useState(initialCode);
  const [phoneNumber, setPhoneNumber] = useState(initialNumber);

  useEffect(() => {
    const parsed = parsePhone(value);
    setCountryCode(parsed.countryCode);
    setPhoneNumber(parsed.number);
  }, [value]);

  const handleCountryChange = (code) => {
    setCountryCode(code);
    onChange(`${code}${phoneNumber}`);
  };

  const handleNumberChange = (e) => {
    const num = e.target.value.replace(/[^\d]/g, '');
    setPhoneNumber(num);
    onChange(`${countryCode}${num}`);
  };

  const selectedCountry = countryCodes.find(c => c.code === countryCode) || countryCodes[0];

  return (
    <div className={`flex gap-2 ${className}`}>
      <Select value={countryCode} onValueChange={handleCountryChange} disabled={disabled}>
        <SelectTrigger className="w-[120px]" data-testid={dataTestId ? `${dataTestId}-country` : undefined}>
          <SelectValue>
            <span className="flex items-center gap-1">
              <span>{selectedCountry.flag}</span>
              <span className="text-xs">{selectedCountry.code}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {countryCodes.map(cc => (
            <SelectItem key={cc.code} value={cc.code}>
              <span className="flex items-center gap-2">
                <span>{cc.flag}</span>
                <span>{cc.name}</span>
                <span className="text-slate-400 text-xs">{cc.code}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="tel"
        value={phoneNumber}
        onChange={handleNumberChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="flex-1"
        data-testid={dataTestId}
      />
    </div>
  );
};

export default PhoneInput;
